import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Film, Briefcase, Share2, Video, Tv, Camera, BookOpen, Clapperboard, CheckCircle, Globe, Star, ArrowRight, Phone, MessageCircle } from 'lucide-react';
import './ServicesPage.css';

// Services data
const services = [
  { id: 1, title: "Wedding Films", description: "Cinematic wedding highlights, full films, short films, and teaser editing", icon: Film, category: "wedding" },
  { id: 2, title: "Corporate Videos", description: "Professional corporate presentations, promotional videos, and brand stories", icon: Briefcase, category: "corporate" },
  { id: 3, title: "Social Media Content", description: "Instagram Reels, YouTube videos, engaging social media content editing", icon: Share2, category: "social-media" },
  { id: 4, title: "Documentary Films", description: "Compelling documentary storytelling and professional film editing", icon: Video, category: "documentary" },
  { id: 5, title: "AD Films", description: "Creative advertisement films and commercial video editing", icon: Tv, category: "commercial" },
  { id: 6, title: "Event Highlights", description: "Event coverage, highlights, and teaser videos for any occasion", icon: Camera, category: "events" },
  { id: 7, title: "Education Videos", description: "Educational content, tutorials, and e-learning video editing", icon: BookOpen, category: "education" },
  { id: 8, title: "Short Films", description: "Creative storytelling through expertly edited short films", icon: Clapperboard, category: "short-film" }
];

// Wedding Packages - Same structure for all countries
const weddingPackages = [
  {
    id: 1,
    name: "Essential Wedding Edit",
    tagline: "Perfect Start",
    features: [
      "Wedding Highlight (3–5 minutes)",
      "Ceremony Film (Including speeches up to 2 hours)",
      "Clean cuts & color correction",
      "Background music & audio sync",
      "1 structured revision round",
      "Full HD / 4K delivery"
    ],
    popular: false
  },
  {
    id: 2,
    name: "Studio Wedding Edit",
    tagline: "Most Popular",
    features: [
      "Wedding Highlight (3–5 minutes)",
      "Ceremony Film (Including speeches)",
      "Full Event Reel (Instagram style)",
      "Enhanced color grading",
      "Music & sound balance",
      "2 structured revision rounds",
      "Social media optimized delivery"
    ],
    popular: true
  },
  {
    id: 3,
    name: "Cinematic Wedding Story",
    tagline: "Premium Choice",
    features: [
      "Cinematic Highlight (7 minutes)",
      "Ceremony Film (Up to 2 hours)",
      "Wedding Teaser (1–2 minutes)",
      "Documentary Film (35–45 minutes)",
      "Advanced color grading",
      "Story-based cinematic editing",
      "2–3 revision rounds"
    ],
    popular: false
  },
  {
    id: 4,
    name: "Complete Wedding Experience",
    tagline: "Ultimate Package",
    features: [
      "Wedding Teaser (1–2 minutes)",
      "Documentary Film (40–50 minutes)",
      "Full Event Reel (90 sec – Instagram style)",
      "Cinematic color grading",
      "Advanced sound design",
      "Unlimited revisions",
      "Multi-format delivery (Web, TV, Social)"
    ],
    popular: false
  }
];

// Country-wise pricing
const countryPricing = {
  US: {
    currency: 'USD',
    symbol: '$',
    flag: '🇺🇸',
    country: 'United States',
    prices: [450, 520, 1050, 620]
  },
  GB: {
    currency: 'GBP',
    symbol: '£',
    flag: '🇬🇧',
    country: 'United Kingdom',
    prices: [375, 425, 870, 520]
  },
  CA: {
    currency: 'CAD',
    symbol: 'CAD ',
    flag: '🇨🇦',
    country: 'Canada',
    prices: [450, 520, 1050, 620]
  },
  AU: {
    currency: 'AUD',
    symbol: 'AUD ',
    flag: '🇦🇺',
    country: 'Australia',
    prices: [450, 520, 1050, 620]
  },
  AE: {
    currency: 'AED',
    symbol: 'AED ',
    flag: '🇦🇪',
    country: 'UAE / Dubai',
    prices: [1700, 1950, 3900, 2300]
  },
  DEFAULT: {
    currency: 'CUSTOM',
    symbol: '',
    flag: '🌍',
    country: 'Your Region',
    prices: [null, null, null, null]
  }
};

const ServicesPage = () => {
  const [detectedCountry, setDetectedCountry] = useState('DEFAULT');
  const [pricing, setPricing] = useState(countryPricing.DEFAULT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    detectCountry();
  }, []);

  const detectCountry = async () => {
    try {
      // Using free IP geolocation API
      const response = await fetch('https://ipapi.co/json/');
      if (response.ok) {
        const data = await response.json();
        const countryCode = data.country_code;
        
        if (countryPricing[countryCode]) {
          setDetectedCountry(countryCode);
          setPricing(countryPricing[countryCode]);
        } else {
          setDetectedCountry('DEFAULT');
          setPricing(countryPricing.DEFAULT);
        }
      }
    } catch (error) {
      console.error('Country detection failed:', error);
      setDetectedCountry('DEFAULT');
      setPricing(countryPricing.DEFAULT);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, index) => {
    if (price === null) {
      return 'Custom Quote';
    }
    return `${pricing.symbol}${price.toLocaleString()}`;
  };

  return (
    <div className="services-page">
      {/* Hero Section */}
      <div className="page-hero services-hero">
        <div className="container">
          <h1 className="page-title">Our Services</h1>
          <p className="page-description">
            Professional video editing services for filmmakers & studios worldwide
          </p>
        </div>
      </div>

      {/* Services Section */}
      <section className="services-detail section">
        <div className="container">
          <div className="section-header-center">
            <h2 className="section-title">
              What We Offer
            </h2>
            <p className="section-subtitle">
              Specialized editing services tailored to your needs
            </p>
          </div>
          
          <div className="services-grid">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <div key={service.id} className="service-detail-card" data-testid={`service-${service.id}`}>
                  <div className="service-icon">
                    <Icon size={32} />
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </div>
              );
            })}
          </div>

          {/* Other Services CTA */}
          <div className="other-services-cta">
            <div className="cta-content-box">
              <h3>Need Corporate, Social Media, or Other Editing Services?</h3>
              <p>Pricing for non-wedding services is customized based on project scope. Let's discuss your requirements!</p>
              <div className="cta-buttons-row">
                <a href="https://wa.me/919313553726" target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp">
                  <MessageCircle size={20} />
                  <span>WhatsApp Us</span>
                </a>
                <Link to="/contact" className="btn btn-secondary">
                  <Phone size={20} />
                  <span>Contact Us</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wedding Packages Section */}
      <section className="packages section" id="wedding-packages">
        <div className="container">
          <div className="section-header-center">
            <h2 className="section-title">
              Wedding Film Packages
            </h2>
            <p className="section-subtitle">
              Choose the perfect package for your wedding film editing needs
            </p>
          </div>

          {/* Location Notice */}
          <div className="pricing-notice">
            <Globe size={20} />
            <div className="pricing-notice-content">
              <div className="location-detected">
                {loading ? (
                  <span>Detecting your location...</span>
                ) : (
                  <span>{pricing.flag} Prices displayed for <strong>{pricing.country}</strong></span>
                )}
              </div>
              <p>
                All wedding packages follow the same structure worldwide. 
                Final pricing may vary based on footage length, number of events, and creative complexity.
              </p>
            </div>
          </div>

          {/* Packages Grid */}
          <div className="packages-grid">
            {weddingPackages.map((pkg, index) => (
              <div 
                key={pkg.id} 
                className={`package-card ${pkg.popular ? 'popular' : ''}`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.popular && <div className="package-badge"><Star size={14} /> Most Popular</div>}
                <div className="package-header">
                  <span className="package-tagline">{pkg.tagline}</span>
                  <h3 className="package-name">{pkg.name}</h3>
                  <div className="package-price">
                    {pricing.prices[index] === null ? (
                      <span className="custom-quote">Custom Quote</span>
                    ) : (
                      <>
                        <span className="price-label">Starting from</span>
                        <span className="price-value">{formatPrice(pricing.prices[index], index)}</span>
                      </>
                    )}
                  </div>
                </div>
                <ul className="package-features">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="package-feature">
                      <CheckCircle size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="package-footer">
                  {pricing.prices[index] === null ? (
                    <a 
                      href="https://wa.me/919313553726" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`btn ${pkg.popular ? 'btn-primary' : 'btn-secondary'} btn-full`}
                    >
                      Request Custom Quote
                    </a>
                  ) : (
                    <Link 
                      to="/booking" 
                      className={`btn ${pkg.popular ? 'btn-primary' : 'btn-secondary'} btn-full`}
                    >
                      Book This Package
                      <ArrowRight size={18} />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Custom Quote CTA for detected countries */}
          {detectedCountry !== 'DEFAULT' && (
            <div className="custom-pricing-note">
              <p>
                <strong>Need something different?</strong> We offer custom packages tailored to your specific requirements.
              </p>
              <a href="https://wa.me/919313553726" target="_blank" rel="noopener noreferrer" className="btn btn-outline">
                <MessageCircle size={18} />
                Request Custom Quote
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="services-cta section">
        <div className="container">
          <div className="cta-box">
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-description">
              Book a consultation and let's discuss how we can bring your project to life
            </p>
            <div className="cta-buttons-row">
              <Link to="/booking" className="btn btn-primary btn-large">
                Book Now
              </Link>
              <Link to="/portfolio" className="btn btn-outline btn-large">
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
